import { format } from "date-fns";
import { GetReportDataResponse } from "src/services/restApi/queries/report.service";
import { AggregatedDepositData, Deposit, Grouping, RawData } from "../types";
import { TRADES_GROUP_BY_OPTIONS } from "./consts";
import { capitaliseFirstLetter, centsToDollars } from "./utils";

export function getDepositGroupOptions(
  deposits: RawData<Deposit>,
  group: Grouping
) {
  const uniqueValues = new Set<string>();
  deposits.forEach((deposit) => {
    const date = new Date(deposit.createdAt);
    switch (group.groupBy) {
      case "psp":
        uniqueValues.add(deposit.psp || "Unknown PSP");
        break;
      case "client":
        uniqueValues.add(deposit.userId || "Unknown Client");
        break;
      case "time":
        if (group.timeGroup === "month") {
          uniqueValues.add(format(date, "MMMM yyyy"));
        } else if (group.timeGroup === "week") {
          uniqueValues.add(format(date, "yyyy-'W'ww"));
        } else if (group.timeGroup === "day") {
          uniqueValues.add(format(date, "yyyy-MM-dd"));
        }
        break;
    }
  });
  return Array.from(uniqueValues).sort();
}

export function aggregateDepositData(
  groupings: Grouping[],
  deposits: RawData<Deposit>
): AggregatedDepositData[] {
  const groupedData = new Map<string, AggregatedDepositData>();

  deposits.forEach((deposit) => {
    const groupKeyParts = groupings.map((g) => {
      const date = new Date(deposit.createdAt);
      switch (g.groupBy) {
        case "psp":
          return deposit.psp || "Unknown PSP";
        case "time":
          if (g.timeGroup === "month") {
            return format(date, "MMMM yyyy") || "Unknown Time";
          } else if (g.timeGroup === "week") {
            return format(date, "yyyy-'W'ww") || "Unknown Time";
          } else if (g.timeGroup === "day") {
            return format(date, "yyyy-MM-dd") || "Unknown Time";
          }
          return "Unknown Time";
        case "client":
        default:
          return deposit.userId || "Unknown Client";
      }
    });
    const groupKey = groupKeyParts.join(" + ");
    if (!groupedData.has(groupKey)) {
      groupedData.set(groupKey, {
        userId: deposit.userId || "",
        psp: deposit.psp || "",
        numberOfDeposits: 0,
        amount: 0,
        groupKey,
        groupValues: groupKeyParts,
        deposits: [],
      });
    }
    const group = groupedData.get(groupKey);
    if (!group) return;

    group.deposits.push(deposit);
    group.numberOfDeposits++;
    group.amount += centsToDollars(deposit.amount);
  });
  return Array.from(groupedData.values());
}

export function createDepositsCsv(
  groupings: Grouping[],
  data: GetReportDataResponse,
  aggregatedData: AggregatedDepositData[]
): string {
  let csvContent = "";
  if (groupings.length === 0 && data.data) {
    const headers = [
      "Deposit ID",
      "Client ID",
      "Deposit Status",
      "PSP",
      "PSP Transaction ID",
      "Currency Type",
      "Amount (USD)",
      "Local Amount",
      "Local Currency",
      "Exchange Rate",
      "Created At",
      "Updated At",
      "Admin Action",
      "Admin Reason",
      "Admin ID",
    ];

    csvContent = [
      headers.join(","),
      ...(data.data as RawData<Deposit>).map((deposit) => {
        return [
          `${deposit.depositId.toLocaleString()}`,
          `${deposit.userId.toLocaleString()}`,
          `${capitaliseFirstLetter(
            deposit.transactionStatus.toLocaleString().toLowerCase()
          )}`,
          `${deposit.psp || "-"}`,
          `${deposit.pspTransactionId || "-"}`,
          `${deposit.currencyType || "-"}`,
          `${deposit.amount / 100}`,
          `${(deposit.localAmount || 0) / 100}`,
          `${deposit.localCurrency || "-"}`,
          `${deposit.exchangeRate || "-"}`,
          `${new Date(deposit.createdAt).toISOString()}`,
          `${
            deposit.updatedAt ? new Date(deposit.updatedAt).toISOString() : "-"
          }`,
          `${deposit.adminAction ?? "-"}`,
          `${deposit.adminReason ?? "-"}`,
          `${deposit.adminId ?? "-"}`,
        ].join(",");
      }),
    ].join("\n");
  } else {
    // Get the filtered data
    const filteredData = aggregatedData.filter((row) => {
      const keyParts = row.groupKey.split(" + ");
      return groupings.every((g, idx) => {
        const filter: string[] = g.groupFilter;
        if (!filter || filter.length === 0) return true;
        return filter.includes(keyParts[idx]);
      });
    });

    // Sort filteredData by all group columns (groupValues)
    filteredData.sort((a, b) => {
      for (let i = 0; i < a.groupValues.length; i++) {
        if (a.groupValues[i] < b.groupValues[i]) return -1;
        if (a.groupValues[i] > b.groupValues[i]) return 1;
      }
      return 0;
    });

    // Prepare headers
    const headers = [
      ...groupings.map(
        (g) =>
          TRADES_GROUP_BY_OPTIONS.find((opt) => opt.value === g.groupBy)
            ?.name || g.groupBy
      ),
      "Number of Deposits",
      "Amount (USD)",
    ];

    // Prepare CSV content
    csvContent = [
      headers.join(","),
      ...filteredData.map((row) => {
        return [
          ...row.groupValues.map((val) => `"${val}"`),
          `"${row.numberOfDeposits.toLocaleString()}"`,
          `"$${row.amount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}"`,
        ].join(",");
      }),
    ].join("\n");
  }

  return csvContent;
}
